import React, {Fragment, useRef, useEffect} from 'react';
import ReactDOM from 'react-dom';
import {Provider} from 'react-redux';
import {Helmet} from 'react-helmet';
import {BrowserRouter, Switch, Route, matchPath} from 'react-router-dom';

import {ThemeProvider} from 'styled-components';
import commonHelmetJson from 'common-helmet-json';

import createStore from '@ibibo/create-redux-store';
import Header from '@ibibo/header';
import Footer from '@ibibo/footer';
import Modal from '@ibibo/modal';
import {defaultTheme} from '@ibibo/build-theme';
import {GlobalStyles} from '@ibibo/global-style';
import {AnalyticsServiceProvider, initializeAdobeDataLayer} from '@ibibo/analytics-provider';

// Common user widget integeration
// Ping did mount integeration
// Component did catch error tracking
// Modal support

const App = ({routes, store, theme, style, analyticsConfig}) => {
  let hideHeader = false;
  let hideFooter = false;
  let showModal = false;

  const RouteComponent = props => {
    const {location} = props;
    const {pathname} = location;
    const previousLocationRef = useRef();
    useEffect(() => {
      previousLocationRef.current = location;
    });
    const previousLocation = previousLocationRef.current;
    routes.some(route => {
      const matchObj = matchPath(pathname, route);
      if (matchObj) {
        const {showHeader = true, showFooter = true, isModal = false} = route;
        hideHeader = !showHeader;
        hideFooter = !showFooter;
        showModal = isModal;
      }
      return matchObj;
    });
    return (
      <Fragment>
        {(!hideHeader && <Header location={window.location} />) || null}
        {(showModal && (
          <Modal>
            <Switch>
              {routes
                .filter(route => route.isModal)
                .map((route, i) => (
                  <Route key={`route${i}`} {...route} />
                ))}
            </Switch>
          </Modal>
        )) ||
          null}
        <Switch location={showModal ? previousLocation : location}>
          {routes
            .filter(route => !route.isModal)
            .map((route, i) => (
              <Route key={`route${i}`} {...route} />
            ))}
        </Switch>
        {(!hideFooter && <Footer />) || null}
      </Fragment>
    );
  };

  let styleComponent = <GlobalStyles />;
  if (style) {
    styleComponent = style;
  }

  return (
    <AnalyticsServiceProvider>
      <ThemeProvider theme={theme}>
        <Helmet {...commonHelmetJson} />
        <Provider store={store}>
          {styleComponent}
          <BrowserRouter>
            <Route component={RouteComponent} />
          </BrowserRouter>
        </Provider>
      </ThemeProvider>
    </AnalyticsServiceProvider>
  );
};

export default function mountReactApp({
  routes,
  reducers,
  initialState = (window as any).__initial_state__,
  theme,
  style,
  analyticsConfig = {
    enabled: true,
    debug: process.env.NODE_ENV === 'development',
  },
}) {
  const store = createStore({reducers, initialState});
  const urlPath = window.location.pathname;
  const searchString = window.location.search;

  routes.some(route => {
    const matchObj = matchPath(urlPath, route);
    if (matchObj) {
      const {params = {}} = matchObj;
      const {populateStoreFromUrl = () => {}} = route;
      populateStoreFromUrl({params, searchString}, store.dispatch);
    }
    return matchObj;
  });
  initializeAdobeDataLayer();

  ReactDOM.hydrate(
    <App
      routes={routes}
      store={store}
      theme={theme || defaultTheme}
      style={style}
      analyticsConfig={analyticsConfig}
    />,
    document.getElementById('root'),
  );
  // window.pageSeen('TTI');
}
